import {useDeepMemo} from '@wandb/weave/hookUtils';
import {useEffect, useMemo, useReducer} from 'react';

import {
  type RunInfoFragmentFragment,
  useRunsInfoQuery,
} from '../../../generated/graphql';
import {logError} from '../../../services/errors/errorReporting';
import {useDatadogRum} from '../../../util/datadog/datadogRum';
import * as Filters from '../../../util/filters';
import {type Query, type RunSetQuery} from '../../../util/queryTypes';
import {
  useRampFlagBaselineRuns,
  useRampFlagClientsideDynamicTimeouts,
  useRampFlagPinnedRuns,
  useRampFlagUseBackendGrouping,
  useRampFlagUseBackendSmoothing,
} from '../../../util/rampFeatureFlags';
import {keyToServerPath} from '../../../util/runs';
import {useStablePanelId} from '../../Panel/PanelIDContext';
import {useSharedPanelState} from '../../Panel/SharedPanelStateContext';
import {POINT_VISUALIZATION_OPTIONS} from '../../WorkspaceDrawer/Settings/types';
import {type Range, runsLinePlotTransformQuery} from '../common';
import {useBackendLineConstruction} from '../componentOutliers/useBackendLineConstruction';
import {usePanelConfigContext} from '../PanelConfigContext';
import {usePanelGroupingSettings} from '../RunsLinePlotContext/usePanelGroupingSettings';
import {usePanelSystemMetricsContext} from '../SystemMetrics/PanelSystemMetricsContext';
import {type BucketedLine, type RunsLinePlotConfig} from '../types';
import {bucketedRunsReducer} from './bucketedDataReducer';
import {type BucketedQueryState} from './bucketedQueryManager';
import {checkBackendLinesSupport} from './queries/checks';
import {getQueryType} from './queries/getQueryType';
import {useLoggingInfo} from './useLoggingInfo';
import {bracketHistoriesData, mapEnabledByRunSets} from './util';

const usePanelQuery = (
  pageQuery: Query,
  config: RunsLinePlotConfig,
  queryZoomRange: Range,
  skip?: boolean
) => {
  const {limit: numRuns, parsedExpressions} = usePanelConfigContext();
  const enableBackendSmoothing = useRampFlagUseBackendSmoothing(
    pageQuery.entityName
  );
  const {isDelayedSystemMetrics} = usePanelSystemMetricsContext();

  const transformed =
    skip || isDelayedSystemMetrics
      ? null
      : runsLinePlotTransformQuery({
          defaultMaxRuns: numRuns,
          enableBackendSmoothing,
          isFullFidelity: true,
          parsedExpressions,
          query: pageQuery,
          runsLinePlotConfig: config,
          // not using this yet, coming in a fast follow
          // useCappedSystemMetrics:
          //   shouldCapSystemMetrics && isSystemMetricsPanel,
          xStepRange: queryZoomRange,
        });

  return useDeepMemo(transformed);
};

export function useBucketedData({
  config,
  pageQuery,
  queryZoomRange,
  nBuckets,
  skip,
}: {
  config: RunsLinePlotConfig;
  pageQuery: Query;
  queryZoomRange: Range;
  nBuckets: number;
  skip?: boolean;
}) {
  const {isAnyRunsetGrouped, getGroupKeysByRunsetId} =
    usePanelGroupingSettings();
  const panelQuery = usePanelQuery(pageQuery, config, queryZoomRange, skip);
  const {bucketQueryManagerById} = useSharedPanelState();
  const canUseClientsideDynamicTimeouts = useRampFlagClientsideDynamicTimeouts(
    pageQuery.entityName
  );

  const groupKeys = useMemo(() => {
    return pageQuery?.runSets != null
      ? pageQuery.runSets.flatMap(runSet => {
          return getGroupKeysByRunsetId(runSet.id);
        })
      : [];
  }, [pageQuery?.runSets, getGroupKeysByRunsetId]);
  const groupKeyNames = useMemo(() => {
    return groupKeys.map(keyToServerPath);
  }, [groupKeys]);

  const [
    {
      aggregatedByRunsetId,
      allRunsetsHaveBackendLines,
      backendLinesDomain,
      isLoading,
      runDataByRunsetId,
      runNames,
      totalRuns,
    },
    bucketedRunsDispatch,
  ] = useReducer(bucketedRunsReducer, {
    aggregatedByRunsetId: {},
    allRunsetsHaveBackendLines: false,
    backendLinesDomain: null,
    loadingByRunSetId: {},
    isLoading: true, // initially set to true so that we don't flash an error on screen for missing data when not loading
    runDataByRunsetId: {},
    runNames: [],
    totalRuns: 0,
  });

  const {
    canUseBackendLineConstruction,
    canUseBackendLineConstructionSmoothing,
    canUseBackendLineConstructionTimeUnits,
  } = useBackendLineConstruction(pageQuery.entityName);
  const enableBackendGrouping = useRampFlagUseBackendGrouping(
    pageQuery.entityName
  );
  const pinnedRunsEnabled = useRampFlagPinnedRuns();
  const baselineRunsEnabled = useRampFlagBaselineRuns();

  const loggingInfo = useLoggingInfo();

  const panelQueryWithGroupKeys = useMemo(() => {
    if (!panelQuery) {
      return null;
    }
    return {
      ...panelQuery,
      groupKeys: groupKeyNames,
      isGrouped: isAnyRunsetGrouped,
      // Clear baseline/pinned IDs if feature flags are disabled
      queries: panelQuery.runsetQueries.map(q => ({
        ...q,
        baselineRunId: baselineRunsEnabled ? q.baselineRunId : undefined,
        pinnedRunIds: pinnedRunsEnabled ? q.pinnedRunIds : undefined,
      })),
    };
  }, [
    panelQuery,
    groupKeyNames,
    isAnyRunsetGrouped,
    baselineRunsEnabled,
    pinnedRunsEnabled,
  ]);

  const datadogRum = useDatadogRum();
  const panelId = useStablePanelId();

  const supportsBackendLineConstruction = checkBackendLinesSupport({
    config,
    context: {
      datadogRum,
      panelId,
      usesBackendLineConstruction: canUseBackendLineConstruction,
    },
    usesBackendLineConstructionSmoothing:
      canUseBackendLineConstructionSmoothing,
    usesBackendLineConstructionTimeUnits:
      canUseBackendLineConstructionTimeUnits,
  });
  const allowsBackendLineConstruction =
    supportsBackendLineConstruction && canUseBackendLineConstruction;

  useEffect(() => {
    if (!panelQueryWithGroupKeys || !pageQuery.runSets?.length) {
      // If there are no runSets, there should also be no queries in panelQuery.queries. Sometimes, due to behavior in
      // toRunsDataQuery in lib.ts, there will be an item in panelQuery.queries but no runSets. This is because it puts
      // the pageQuery in queries instead of creating a panel query in this case. Either way, if there are no runSets,
      // there is no need to do any querying here so we can eject.
      return;
    }
    const managers = bucketQueryManagerById.current;
    const handlers: ((state: BucketedQueryState) => void)[] = [];

    panelQueryWithGroupKeys.queries.forEach(q => {
      const usesInternalID = !!q.internalProjectId;

      const queryType = getQueryType({
        allowsBackendLineConstruction,
        enableBackendGrouping,
        historyKeys:
          panelQueryWithGroupKeys.bucketedHistorySpecs?.flatMap(
            spec => spec.keys
          ) ?? [],
        isGrouped: isAnyRunsetGrouped,
        runSets: pageQuery?.runSets ?? [],
        usesInternalId: usesInternalID,
      });

      const handlerDataChange = (state: BucketedQueryState) => {
        const bracketedState = bracketHistoriesData(state, config.xAxis);

        bucketedRunsDispatch({
          type: 'bucketedRuns/combo',
          data: bracketedState,
          isAggregated: !!state.data?.isAggregated,
          loading: state.loading,
          runsetId: q.id,
          totalRuns: state.data?.totalRuns,
        });
      };

      handlers.push(handlerDataChange);

      const matchedQueryManager = bucketQueryManagerById.current[q.id];
      if (matchedQueryManager) {
        matchedQueryManager.setDynamicTimeoutsEnabled(
          canUseClientsideDynamicTimeouts
        );
        matchedQueryManager.registerRequest({
          expressions: config.expressions,
          handler: handlerDataChange,
          nBuckets,
          queryType,
          runsDataQuery: panelQueryWithGroupKeys,
          singleQuery: q,
          loggingInfo,
        });
      } else {
        const {queries, ...pQuery} = panelQueryWithGroupKeys;

        const error = new ReferenceError(
          'Full fidelity error: no bucketed query manager found for query'
        );
        const context = {
          bucketedQueryManager: Object.keys(
            bucketQueryManagerById.current
          ).join(', '),
          panelQuery: pQuery,
          queries: q,
        };
        // @ts-ignore Sentry can accept key/value pairs
        logError(error, context);
      }
    });

    // Cleanup function that unregisters all handlers
    return () => {
      panelQueryWithGroupKeys.queries.forEach((q, index) => {
        if (managers[q.id]) {
          managers[q.id].unregisterRequest(handlers[index]);
        }
      });
    };
  }, [
    allowsBackendLineConstruction,
    bucketQueryManagerById,
    canUseBackendLineConstruction,
    canUseClientsideDynamicTimeouts,
    config.expressions,
    config.xAxis,
    enableBackendGrouping,
    groupKeyNames,
    isAnyRunsetGrouped,
    loggingInfo,
    nBuckets,
    pageQuery?.runSets,
    panelQueryWithGroupKeys,
  ]);

  const {
    data: runsInfoData,
    loading: runsInfoLoading,
    error: runsInfoError,
  } = useRunsInfoQuery({
    variables: {
      projectName: pageQuery.projectName,
      entityName: pageQuery.entityName,
      filters: JSON.stringify(
        Filters.toMongo(Filters.runNamesFilter(runNames))
      ),
    },
    skip: runNames.length === 0,
  });

  // Memoize enabled keys to prevent unnecessary recalculations
  const enabledKeys = useMemo(() => {
    const runSets = mapEnabledByRunSets(pageQuery.runSets ?? []);
    return Object.keys(runDataByRunsetId).filter(key => runSets[key] ?? false);
  }, [runDataByRunsetId, pageQuery.runSets]);

  const runInfoMap: Partial<
    Record<string, RunInfoFragmentFragment['runInfo']>
  > = useMemo(() => {
    return (
      runsInfoData?.project?.runs?.edges?.reduce(
        (acc, edge) => {
          acc[edge.node.id] = edge.node?.runInfo;
          return acc;
        },
        {} as Partial<Record<string, RunInfoFragmentFragment['runInfo']>>
      ) ?? {}
    );
  }, [runsInfoData]);

  // Memoize individual run data processing
  const processedRunData = useMemo(() => {
    // Create a map of runset ID to query for quick lookup
    const runsetQueryMap = (pageQuery.runSets || []).reduce(
      (acc, query) => {
        acc[query.id] = query;
        return acc;
      },
      {} as Record<string, RunSetQuery>
    );

    return enabledKeys.reduce(
      (acc, key) => {
        const data = runDataByRunsetId[key];
        const runsetQuery = runsetQueryMap[key];

        // Get baseline/pinned IDs from this specific runset
        const baselineRunId = runsetQuery?.baselineRunId;
        const pinnedRunIds = runsetQuery?.pinnedRunIds || [];

        try {
          const runs = Object.values(data.data?.runsById ?? {})
            .filter(r => !!r)
            .map(r => {
              const isBaseline = r.id === baselineRunId;
              const isPinned = pinnedRunIds.includes(r.id);

              return {
                ...r,
                runInfo: runInfoMap[r.id] ?? undefined,
                isBaseline,
                isPinned,
              };
            });
          acc[key] = runs;
        } catch (e) {
          console.error(e);
          acc[key] = [];
        }
        return acc;
      },
      {} as Record<string, BucketedLine[]>
    );
  }, [enabledKeys, runDataByRunsetId, runInfoMap, pageQuery.runSets]);

  // Create stable histories data reference
  const historyData = useMemo(() => {
    return enabledKeys.flatMap(key => processedRunData[key]);
  }, [enabledKeys, processedRunData]);

  const bucketedDataMemo = useMemo(() => {
    return {
      _dataType: POINT_VISUALIZATION_OPTIONS.BucketingGorilla,
      allRunsetsHaveBackendLines,
      backendLinesDomain,
      entityName: pageQuery.entityName,
      histories: {data: historyData},
      initialLoading: false,
      isAggregated: aggregatedByRunsetId,
      projectName: pageQuery.projectName,
      totalRuns: totalRuns,
    };
  }, [
    aggregatedByRunsetId,
    allRunsetsHaveBackendLines,
    backendLinesDomain,
    historyData,
    pageQuery.entityName,
    pageQuery.projectName,
    totalRuns,
  ]);

  const {error} = useMemo(() => {
    return {
      error:
        Object.values(runDataByRunsetId).find(v => v.error) ||
        runsInfoError?.networkError,
    };
  }, [runDataByRunsetId, runsInfoError]);

  if (error) {
    console.error(error);
  }

  /**
   * IMPORTANT!
   *
   *
   * Generates a cache key for bucketed data results.
   * This key is used to uniquely identify the data in the cache.
   * It should be updated whenever any of the dependencies that affect
   * the data query change.
   *
   * Do not change the cache key here unless you know what you are doing.
   * The cache key should be the panelQuery. If you need to update the cache key
   * to add any additional dependencies to trigger invalidation, add them to
   * the panelQuery.
   */

  const ultimateLoading = isLoading || runsInfoLoading;
  return useMemo(
    () => ({
      data: bucketedDataMemo,
      error,
      loading: ultimateLoading,
      key: panelQueryWithGroupKeys,
    }),
    [bucketedDataMemo, error, panelQueryWithGroupKeys, ultimateLoading]
  );
}
